<template>
    <div class="position-relative d-flex flex-column w-100">
        <template v-if="status == 'ready'">
            <div
                ref="body"
                class="h-100 flex-grow-1 overflow-y-auto pdf--body"
                @scroll="scroll"
            >
                <template v-if="pdf">
                    <pdf-page
                        v-for="number in pdf.numPages"
                        ref="pages"
                        v-bind="{
                            fit,
                            bodyWidth,
                            bodyHeight,
                            number,
                            pdfjs,
                            pdf,
                            visible: isVisible(number),
                        }"
                        :key="number"
                        class="mtsp-12"
                        @goto-page="gotoPage"
                        @goto-destination="gotoDestination"
                    />
                </template>
            </div>

            <div
                v-if="pdf"
                class="d-flex align-items-center rounded-sm font--14 lh--18 py-8 px-12 bottom mb-32 position-absolute abs-center-x bg--black--80 text-light"
            >
                <span class="text-gray-medium">
                    {{
                        ucfirst(
                            $t("common.pageXofY", {
                                x: currentPage,
                                y: pdf.numPages,
                            })
                        )
                    }}
                </span>

                <button-link
                    :disabled="upButtonDisabled"
                    icon="angle-up"
                    theme="light"
                    @click="up"
                />

                <button-link
                    :disabled="downButtonDisabled"
                    icon="angle-down"
                    theme="light"
                    @click="down"
                />

                <button-link
                    v-if="$mq.sPlus"
                    class="ml-32"
                    :icon="fit == 'width' ? 'arrow-shrink' : 'arrows-shrink-h'"
                    theme="light"
                    @click="toggleFit"
                />
            </div>
        </template>

        <div
            v-else-if="status == 'error'"
            class="p-32 text-center"
        >
            {{ ucfirst($t("common.loadingError", { what: "pdf" })) }}
        </div>

        <spinner-block v-else-if="status == 'loading'" />
    </div>
</template>

<script>
import { toRaw } from "vue"
import debounce from "lodash/debounce"
import isNumber from "lodash/isNumber"
import maxBy from "lodash/maxBy"
import { ucfirst } from "~/lib/utils"
import { useListenerManager } from "front-toolkit/plugins/ListenersPlugin"

import ButtonLink from "~/components/buttons/ButtonLink"
import PdfPage from "./PdfPage"
import SpinnerBlock from "~/components/elements/SpinnerBlock"

export default {
    props: {
        url: {
            type: String,
            required: true,
        },

        withoutCredentials: {
            type: Boolean,
            default: false,
        },
    },

    emits: ["accessed"],

    setup() {
        const { listen } = useListenerManager()
        return { listen }
    },

    data() {
        return {
            pdf: null,
            pdfjs: null,
            fit: "width", // "width" | "height"
            visibilities: {},

            status: "loading", // loading | error | ready

            bodyHeight: 0, // px
            bodyWidth: 0, // px
        }
    },

    computed: {
        currentPage: {
            get() {
                const a = maxBy(
                    Object.entries(this.visibilities),
                    ([_page, v]) => v
                )
                return a ? Number(a[0]) : 0
            },

            set(val) {
                this.goto(val)
            },
        },

        upButtonDisabled() {
            return this.currentPage <= 1
        },

        downButtonDisabled() {
            if (!this.pdf?._pdfInfo) return true
            return this.currentPage >= toRaw(this.pdf)._pdfInfo.numPages
        },
    },

    mounted() {
        this.listen("resize", () => {
            this.updateVisibilities()
            if (this.$mq.xsMinus) {
                this.fit = "width"
            }
        })
    },

    created() {
        this.updateVisibilities = debounce(this.updateVisibilities, 60)
        this.goto = debounce(this.goto, 60)
        this.fetchPDF()
    },

    beforeUnmount() {
        if (this.pdf) {
            toRaw(this.pdf)?.destroy()
            this.pdf = null
        }
        this.updateVisibilities.cancel()
        this.goto.cancel()
    },

    methods: {
        async fetchPDF() {
            const module = await import("./pdf.js")
            const pdfjs = module.default

            try {
                const pdf = await pdfjs.getDocument({
                    url: this.url,
                    withCredentials: !this.withoutCredentials,
                }).promise
                this.pdfjs = pdfjs
                this.pdf = pdf
                this.status = "ready"
                this.updateVisibilities()
                this.$emit("accessed")
            } catch (_) {
                this.status = "error"
            }
        },

        scroll() {
            this.updateVisibilities()
        },

        toggleFit() {
            if (this.fit == "height") {
                this.fit = "width"
            } else {
                this.fit = "height"
            }
        },

        isVisible(pageIdx) {
            return this.visibilities[pageIdx] > -1
        },

        goto(num) {
            const pages = this.$refs.pages.map(p => p.$el)
            const body = this.$refs.body
            const top = pages[num - 1].offsetTop

            body.scrollTo(0, top)
        },

        async gotoPage(page) {
            const num =
                typeof page == "number"
                    ? page
                    : await toRaw(this.pdf).getPageIndex(page)
            this.goto(num + 1)
        },

        async gotoDestination(destination) {
            let page, num
            if (typeof destination === "string") {
                const explicitDest = await toRaw(this.pdf).getDestination(
                    destination
                )
                page = explicitDest[0]
                num = await toRaw(this.pdf).getPageIndex(page)
                this.goto(num + 1)
            } else if (
                typeof destination[0] === "object" &&
                destination[0] !== null
            ) {
                page = destination[0]
                num = await toRaw(this.pdf).getPageIndex(page)
                this.goto(num + 1)
            } else if (isNumber(destination[0])) {
                // TODO: this one doesn't seem to work right
                this.goto(destination[0])
            }
        },

        up() {
            this.currentPage = Math.max(1, this.currentPage - 1)
        },

        down() {
            const n = Math.min(this.currentPage + 1, toRaw(this.pdf).numPages)
            this.currentPage = n
        },

        updateVisibilities() {
            if (!this.$refs.pages || !this.$refs.body) return

            const pages = this.$refs.pages.map(p => p.$el)
            const body = this.$refs.body

            const top = body.scrollTop
            const height = body.clientHeight
            const bottom = top + height

            const visibilities = {}

            for (let i = 0; i < pages.length; i++) {
                const element = pages[i]

                const t = element.offsetTop
                const h = element.offsetHeight
                const b = t + h

                const vt = Math.max(t, top)
                const vb = Math.min(b, bottom)

                const v = (vb - vt) / h

                visibilities[i + 1] = v
            }

            this.visibilities = visibilities
            this.bodyHeight = height
            this.bodyWidth = body.clientWidth
        },

        ucfirst,
    },

    components: {
        ButtonLink,
        PdfPage,
        SpinnerBlock,
    },
}
</script>

<style scoped>
.pdf--body {
    -webkit-overflow-scrolling: touch;
    scroll-behavior: smooth;
}
</style>
